/*
 * Paywall — restful night-mode $7.77/mo subscription card.
 *
 * Design contract (per user spec):
 *   – Backdrop        : #121212
 *   – Card            : #1C1E21
 *   – Price border    : #D4AF37 (Burnished Gold), thin
 *   – Price text      : #FDFBF7 (Soft Alabaster White)
 *   – Scripture anchor: #D2B48C (Sand) — "A Covenant of Completeness, Order, and Rest."
 *   – Explainer body  : #FDFBF7 subdued (~85% alpha)
 *
 * This component is rendered in two shapes:
 *   – as a full-screen paywall (used by Login when trial expired, and by any
 *     app screen wrapped in <RequireSubscription>)
 *   – as a card variant (embedded into a Card by the caller) — via the
 *     `variant="embedded"` prop.
 *
 * On iOS builds we do NOT show inline Stripe checkout (see utils/platform.js).
 * We swap the CTA for an external web link to keep App Review 3.1.1 clean.
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Loader2, ChevronDown, ChevronUp, ShieldCheck, RotateCcw, X, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { paywallCopy, canShowInlineStripeCheckout, shouldUseAppleIAP, getDisplayPriceUSD, showCovenantTheology, isIOS } from '../utils/platform';
import * as iap from '../utils/iapBridge';

const COLORS = {
  bg: '#121212',
  card: '#1C1E21',
  gold: '#D4AF37',
  goldSoft: 'rgba(212, 175, 55, 0.35)',
  alabaster: '#FDFBF7',
  sand: '#D2B48C',
  fgSubtle: 'rgba(253, 251, 247, 0.72)',
  fgMuted: 'rgba(253, 251, 247, 0.52)',
  hairline: 'rgba(253, 251, 247, 0.08)',
};

/**
 * @param {Object} props
 * @param {'fullscreen' | 'embedded'} [props.variant='fullscreen']
 * @param {() => void} [props.onDismiss]  — shows a close button when provided (only in fullscreen)
 * @param {string} [props.headline]       — override title text
 * @param {string} [props.subheadline]    — override subtitle
 */
export default function Paywall({
  variant = 'fullscreen',
  onDismiss,
  headline,
  subheadline,
}) {
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const copy = paywallCopy();

  useEffect(() => {
    let mounted = true;
    // On iOS we intentionally do NOT call /api/stripe/config — the price
    // comes from Apple's StoreKit catalog, not from our backend. Making
    // this call on iOS wastes a network round-trip and, when Stripe is
    // unconfigured on the backend, can surface a scary error toast on
    // the subscription page. Reviewer on iPad Air (2.1(b) rejection,
    // Aug 21 2026) reported "the app displayed an error message when
    // we tried to access the subscription page" — this early-return is
    // the fix. We seed config with the Apple-tier price directly.
    if (isIOS()) {
      setConfig({ configured: false, price_usd: getDisplayPriceUSD() });
      setLoadingConfig(false);
      return undefined;
    }
    api
      .getStripeConfig()
      .then((c) => {
        if (mounted) setConfig(c);
      })
      .catch(() => {
        if (mounted) setConfig({ configured: false, price_usd: getDisplayPriceUSD() });
      })
      .finally(() => mounted && setLoadingConfig(false));
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubscribe = async () => {
    // ---- Defense-in-depth iOS detection (see TrialBanner for rationale) ----
    // Compute FOUR independent iOS signals. If ANY of them fire we treat
    // the runtime as iOS and refuse to navigate to Stripe (that would
    // hang the WKWebView on the launch splash — bug reported Aug 15 2026).
    const decidedApple = copy.showAppleIAP || shouldUseAppleIAP();
    const bridgeAvail = iap.isNativeIAPAvailable();
    const uaLower =
      typeof navigator !== 'undefined'
        ? (navigator.userAgent || '').toLowerCase()
        : '';
    const uaMarksIOS =
      uaLower.includes('pwashell') ||
      /iphone|ipad|ipod/.test(uaLower);
    const wkBridgeExists =
      typeof window !== 'undefined' &&
      !!(
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.selahIAP
      );
    const looksLikeIOS =
      decidedApple || bridgeAvail || uaMarksIOS || wkBridgeExists;

    if (typeof window !== 'undefined' && typeof window.__iapDebug === 'function') {
      window.__iapDebug(
        `Paywall tap · shouldUseAppleIAP=${decidedApple} bridgeAvail=${bridgeAvail} uaIOS=${uaMarksIOS} wkBridge=${wkBridgeExists}`
      );
    }

    // iOS branch: purchase via Apple StoreKit (App Review 3.1.1 requires this).
    if (looksLikeIOS) {
      setSubscribing(true);
      try {
        if (!bridgeAvail) {
          if (typeof window.__iapDebug === 'function') {
            window.__iapDebug('iOS but bridge missing — blocking Stripe fallback');
          }
          toast.error('App Store purchase unavailable', {
            description:
              'Please make sure you are running the latest version of the app.',
          });
          setSubscribing(false);
          return;
        }
        const productId =
          process.env.REACT_APP_APPLE_PRODUCT_ID ||
          'com.selahdownloads.subscription.monthly';
        if (typeof window.__iapDebug === 'function') {
          window.__iapDebug(`Calling iap.purchase(${productId})`);
        }
        const result = await iap.purchase(productId);
        if (typeof window.__iapDebug === 'function') {
          try {
            window.__iapDebug(
              `iap.purchase resolved · ok=${result?.ok} cancelled=${result?.cancelled} err=${result?.error || 'none'} hasJWS=${!!result?.signedTransaction}`
            );
          } catch (_e) { /* ignore */ }
        }
        if (!result.ok) {
          if (result.cancelled) {
            // User dismissed the Apple sheet — no-op, no toast.
            setSubscribing(false);
            return;
          }
          toast.error('Purchase could not complete', {
            description: result.error || 'Please try again in a moment.',
          });
          setSubscribing(false);
          return;
        }
        // Forward the signed JWS to the backend for verification.
        if (typeof window.__iapDebug === 'function') {
          window.__iapDebug('POST /api/apple/verify-transaction …');
        }
        const verify = await api.verifyAppleTransaction({
          signed_transaction: result.signedTransaction,
          signed_renewal_info: result.signedRenewalInfo || null,
        });
        if (typeof window.__iapDebug === 'function') {
          try {
            window.__iapDebug(
              `verify response · is_active=${verify?.is_active} provider=${verify?.provider || 'n/a'} err=${verify?.error || 'none'}`
            );
          } catch (_e) { /* ignore */ }
        }
        if (verify?.is_active) {
          toast.success('Welcome to Selah', {
            description: 'Your subscription is active. Full access unlocked.',
          });
          setTimeout(() => window.location.reload(), 900);
        } else {
          toast.error('Purchase completed but activation failed', {
            description: 'Please contact support@selahdownloads.com.',
          });
          setSubscribing(false);
        }
      } catch (e) {
        toast.error('Purchase failed', { description: e?.message });
        setSubscribing(false);
      }
      return;
    }

    // Non-iOS: Stripe web checkout (unchanged).
    if (!canShowInlineStripeCheckout()) {
      return; // guard — should never hit given the branch above
    }
    setSubscribing(true);
    try {
      if (typeof window.__iapDebug === 'function') {
        window.__iapDebug('Non-iOS — creating Stripe checkout session');
      }
      const res = await api.createStripeCheckoutSession(window.location.origin);
      if (!res?.url) throw new Error('No checkout URL returned');
      if (res.already_subscribed) {
        toast.info('You already have an active subscription', {
          description: 'Opening Manage Subscription…',
        });
      }
      setTimeout(() => {
        window.location.href = res.url;
      }, res.already_subscribed ? 900 : 0);
    } catch (e) {
      toast.error('Could not start checkout', { description: e?.message });
      setSubscribing(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      // iOS build: use Apple StoreKit restore instead of Stripe.
      if (shouldUseAppleIAP()) {
        if (!iap.isNativeIAPAvailable()) {
          toast.error('Restore is only available in the latest app version.');
          setRestoring(false);
          return;
        }
        const result = await iap.restore();
        if (!result.ok || !Array.isArray(result.signedTransactions) || result.signedTransactions.length === 0) {
          toast('No previous purchases found', {
            description: 'If you subscribed before, sign in with that same Apple ID.',
          });
          setRestoring(false);
          return;
        }
        const verify = await api.restoreAppleTransactions({
          signed_transactions: result.signedTransactions,
        });
        if (verify?.is_active) {
          toast.success('Subscription restored', {
            description: 'Welcome back — full access is now unlocked.',
          });
          setTimeout(() => window.location.reload(), 900);
        } else {
          toast('No active subscription to restore', {
            description: 'You can subscribe below, or contact support if this seems wrong.',
          });
          setRestoring(false);
        }
        return;
      }

      // Non-iOS: Stripe restore (unchanged).
      const res = await api.restoreStripePurchases();
      if (res?.restored) {
        toast.success('Subscription restored', {
          description: 'Welcome back — full access is now unlocked.',
        });
        setTimeout(() => window.location.reload(), 900);
      } else {
        toast(
          res?.message || 'No active subscription found for this email.',
          { description: 'You can subscribe below, or try a different email.' }
        );
      }
    } catch (e) {
      toast.error('Restore failed', { description: e?.message });
    } finally {
      setRestoring(false);
    }
  };

  const price = config?.price_usd ?? getDisplayPriceUSD();
  // On iOS the Stripe config still returns $7.77, but Apple's product
  // catalog is priced at $7.79 (closest available Apple tier). Always
  // display the platform-correct price so users never see a number that
  // differs from what Apple's purchase sheet will charge them.
  const displayPrice = shouldUseAppleIAP() ? getDisplayPriceUSD() : price;
  const priceDisplay = `$${Number(displayPrice).toFixed(2)}`;

  /* ─────────────── iOS silent-lock variant ─────────────── */
  /* Renders a locked account state with NO price, NO CTA, NO external
     purchase mention — the only compliant paywall shape on iOS.        */
  const silentLockShell = (
    <div
      data-testid="paywall-ios-silent-lock"
      className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden"
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.hairline}`,
        boxShadow: '0 24px 60px -20px rgba(0,0,0,0.55), 0 6px 20px -10px rgba(0,0,0,0.4)',
      }}
    >
      <div
        aria-hidden
        className="absolute top-0 left-8 right-8 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${COLORS.goldSoft}, transparent)` }}
      />
      <div className="px-7 pt-10 pb-8 text-center">
        <div
          className="mx-auto mb-5 w-14 h-14 rounded-full flex items-center justify-center"
          style={{
            background: 'rgba(212, 175, 55, 0.06)',
            border: `1px solid ${COLORS.hairline}`,
          }}
        >
          <Lock size={22} style={{ color: COLORS.sand }} />
        </div>
        <p
          className="text-[11px] uppercase tracking-[0.32em] mb-3"
          style={{ color: COLORS.sand }}
        >
          Selah Downloads
        </p>
        <h2
          className="text-2xl font-semibold leading-tight mb-3"
          style={{ color: COLORS.alabaster, fontFamily: '"Playfair Display", serif' }}
        >
          {copy.lockTitle}
        </h2>
        <p
          className="text-sm leading-relaxed mb-6"
          style={{ color: COLORS.fgSubtle }}
          data-testid="paywall-ios-lock-body"
        >
          {copy.lockBody}
        </p>

        {/* Restore purchases — the ONLY compliant action on iOS */}
        <button
          type="button"
          data-testid="paywall-restore-button"
          onClick={handleRestore}
          disabled={restoring}
          className="w-full flex items-center justify-center gap-2 h-11 rounded-xl text-[13.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(212,175,55,0.5)]"
          style={{
            color: COLORS.alabaster,
            border: `1px solid ${COLORS.hairline}`,
            background: 'rgba(253, 251, 247, 0.04)',
          }}
        >
          {restoring ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Checking…
            </>
          ) : (
            <>
              <RotateCcw size={14} />
              Restore purchases
            </>
          )}
        </button>

        <div
          className="mt-6 flex items-center justify-center gap-1.5 text-[11px]"
          style={{ color: COLORS.fgMuted }}
        >
          <ShieldCheck size={12} style={{ color: COLORS.sand }} />
          <span>Encrypted. Private. Yours.</span>
        </div>
      </div>
    </div>
  );

  /* ─────────────── Standard paywall (Web / Android / Windows) ─────────────── */
  const cardShell = (
    <div
      data-testid="paywall-card"
      className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden"
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.hairline}`,
        boxShadow: '0 24px 60px -20px rgba(0,0,0,0.55), 0 6px 20px -10px rgba(0,0,0,0.4)',
      }}
    >
      {/* subtle top gold hairline */}
      <div
        aria-hidden
        className="absolute top-0 left-8 right-8 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${COLORS.goldSoft}, transparent)` }}
      />

      <div className="px-7 pt-8 pb-7">
        {/* Header */}
        <div className="text-center mb-6">
          <p
            className="text-[11px] uppercase tracking-[0.32em] mb-3"
            style={{ color: COLORS.sand }}
          >
            Selah Downloads
          </p>
          <h2
            className="text-2xl font-semibold leading-tight mb-2"
            style={{ color: COLORS.alabaster, fontFamily: '"Playfair Display", serif' }}
          >
            {headline || 'Step into the rhythm of rest.'}
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: COLORS.fgSubtle }}>
            {subheadline ||
              'Continue capturing the moments God is speaking. Your encrypted downloads and history are waiting.'}
          </p>
        </div>

        {/* Price container — thin gold-bordered card */}
        <div
          data-testid="paywall-price-container"
          className="relative mx-auto mb-4 py-6 px-6 rounded-xl text-center"
          style={{
            border: `1px solid ${COLORS.gold}`,
            background:
              'linear-gradient(180deg, rgba(212,175,55,0.06) 0%, rgba(212,175,55,0.015) 100%)',
          }}
        >
          <div className="flex items-baseline justify-center gap-1">
            <span
              className="text-[44px] leading-none font-bold"
              style={{
                color: COLORS.alabaster,
                fontFamily: '"Playfair Display", serif',
                letterSpacing: '-0.02em',
              }}
              data-testid="paywall-price"
            >
              {priceDisplay}
            </span>
            <span
              className="text-sm"
              style={{ color: COLORS.fgSubtle, marginLeft: 4 }}
            >
              /mo
            </span>
          </div>
          {/* Scriptural anchor — hidden on iOS because Apple's tier ladder
              cannot express $7.77, so the 7·7·7 theology only renders when
              the price is actually $7.77 (web / Android / Windows). */}
          {showCovenantTheology() && (
            <p
              className="mt-3 text-[13px] italic leading-snug"
              style={{ color: COLORS.sand, fontFamily: '"Crimson Text", serif' }}
              data-testid="paywall-anchor-text"
            >
              A Covenant of Completeness, Order, and Rest.
            </p>
          )}
        </div>

        {/* "Why $7.77?" collapsible explainer — hidden on iOS for the same
            reason as the scriptural anchor above. */}
        {showCovenantTheology() && (
          <>
            <button
              type="button"
              data-testid="paywall-why-toggle"
              onClick={() => setWhyOpen((v) => !v)}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-[12px] tracking-wide transition-colors rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(212,175,55,0.5)]"
              style={{ color: COLORS.fgMuted }}
            >
              <span>Why $7.77?</span>
              {whyOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>

            <AnimatePresence initial={false}>
              {whyOpen && (
                <motion.div
                  key="why-explainer"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.28, ease: 'easeOut' }}
                  data-testid="paywall-why-body"
                  className="overflow-hidden"
                >
                  <div
                    className="mt-2 mb-4 px-4 py-3 rounded-lg text-[12.5px] leading-relaxed"
                    style={{
                      background: 'rgba(212, 175, 55, 0.045)',
                      border: `1px solid ${COLORS.hairline}`,
                      color: COLORS.fgSubtle,
                    }}
                  >
                    In Scripture, <span style={{ color: COLORS.sand }}>seven</span> is the number
                    of completeness — the day God rested, the crown of creation.
                    Three sevens (<span style={{ color: COLORS.sand }}>7·7·7</span>) mark divine
                    perfection, order, and rest. Your subscription is a small covenant to step
                    into that rhythm every month.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* Subscribe CTA */}
        <Button
          type="button"
          onClick={handleSubscribe}
          disabled={subscribing || loadingConfig}
          data-testid="paywall-subscribe-button"
          className="w-full h-11 font-semibold tracking-wide rounded-xl"
          style={{
            background: `linear-gradient(180deg, ${COLORS.gold} 0%, #B7912D 100%)`,
            color: '#1A1204',
            border: `1px solid ${COLORS.gold}`,
            boxShadow: '0 6px 20px -8px rgba(212, 175, 55, 0.4)',
          }}
        >
          {subscribing ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              {shouldUseAppleIAP() ? 'Contacting App Store…' : 'Opening checkout…'}
            </>
          ) : (
            <>{copy.cta}</>
          )}
        </Button>

        <p
          className="mt-3 text-[11.5px] text-center leading-relaxed"
          style={{ color: COLORS.fgMuted }}
        >
          {copy.helper}
        </p>

        {/* ─────────── App Store compliance: subscription disclosure block
             Required by Apple 3.1.2(a), Google Play, and Microsoft Store —
             price + cycle + auto-renew + cancel + trial + legal must all be
             visible BEFORE purchase.                                       */}
        <div
          data-testid="paywall-subscription-disclosure"
          className="mt-5 px-4 py-3 rounded-lg text-[11.5px] leading-relaxed space-y-1"
          style={{
            background: 'rgba(253, 251, 247, 0.025)',
            border: `1px solid ${COLORS.hairline}`,
            color: COLORS.fgMuted,
          }}
        >
          <p>
            <span style={{ color: COLORS.fgSubtle }}>{priceDisplay} USD / month</span>,
            billed monthly. Auto-renews until you cancel.
          </p>
          <p>
            Cancel anytime from{' '}
            <span style={{ color: COLORS.fgSubtle }}>Settings &rarr; Manage subscription</span>{' '}
            &mdash; no fees, no penalty. Cancellation takes effect at the end of the
            current billing period.
          </p>
          <p className="pt-1">
            By subscribing you agree to our{' '}
            <Link
              to="/terms"
              data-testid="paywall-terms-link"
              className="underline underline-offset-2"
              style={{ color: COLORS.sand }}
            >
              Terms
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              data-testid="paywall-privacy-link"
              className="underline underline-offset-2"
              style={{ color: COLORS.sand }}
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        {/* Secondary actions */}
        <div
          className="mt-5 pt-4 flex flex-col gap-2"
          style={{ borderTop: `1px solid ${COLORS.hairline}` }}
        >
          <button
            type="button"
            data-testid="paywall-restore-button"
            onClick={handleRestore}
            disabled={restoring}
            className="w-full flex items-center justify-center gap-2 h-9 rounded-md text-[12.5px] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(212,175,55,0.5)]"
            style={{
              color: COLORS.fgSubtle,
              border: `1px solid ${COLORS.hairline}`,
              background: 'transparent',
            }}
          >
            {restoring ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                {shouldUseAppleIAP() ? 'Checking App Store…' : 'Checking…'}
              </>
            ) : (
              <>
                <RotateCcw size={13} />
                Restore purchases
              </>
            )}
          </button>

          <div
            className="flex items-center justify-center gap-1.5 text-[11px]"
            style={{ color: COLORS.fgMuted }}
          >
            <ShieldCheck size={12} style={{ color: COLORS.sand }} />
            <span>Encrypted. Cancel anytime. No hidden fees.</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Pick the compliant shell for this platform.
  // Legacy `silentLock` fallback (only reachable if code above sets it — no
  // platform does anymore, but we keep the shell so external callers that
  // pass `silentLock` don't break).
  const activeShell = copy.silentLock ? silentLockShell : cardShell;

  if (variant === 'embedded') {
    return activeShell;
  }

  // Fullscreen variant — dark-mode backdrop
  return (
    <div
      data-testid="paywall-fullscreen"
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
      style={{ background: COLORS.bg }}
    >
      {/* faint grain overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      {onDismiss && (
        <button
          type="button"
          data-testid="paywall-close-button"
          onClick={onDismiss}
          aria-label="Close paywall"
          className="absolute top-4 right-4 h-9 w-9 rounded-full flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(212,175,55,0.5)]"
          style={{
            color: COLORS.fgSubtle,
            background: 'rgba(253, 251, 247, 0.04)',
            border: `1px solid ${COLORS.hairline}`,
          }}
        >
          <X size={16} />
        </button>
      )}

      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full"
      >
        {activeShell}
      </motion.div>
    </div>
  );
}
